import { Flex } from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useFetchUserHasPermissions } from '../../api/user/useFetchUserHasPermissions';
import { useFetchUserPermissions } from '../../api/user/useFetchUserPermissions';
import { useAuth } from '../../hooks/useAuth';
import { useFetchOrganization } from '../../hooks/queries/useFetchOrganization';
import { useOrganizationId } from '../../hooks/utils/useOrganizationId';
import { PermissionUrn } from '../../pages/UserSettings/Roles/api/permissions.type';
import { ActionableCard } from '../ActionableCard/ActionableCard';
import Icon from '../Icon';
import { OrganizationSubscription } from '@manifest-cyber/types/interface/db';
import { hasOrganizationSubscriptions } from '../../lib/hasOrganizationSubscriptions';
import Loading from '../Loading';

type NonEmptyArray<T> = [T, ...T[]];

interface ProtectedRouteProps {
  /**
   * Required permission key for the route.
   * Must be explicitly provided using the ROUTES constant.
   */
  // permissionKey: RoutePermissionKey;
  children: React.ReactNode;
  requiredPermissions: PermissionUrn[];
  availableOnlyForPlatformAdmins?: boolean;
  /**
   * Required subscriptions for the route.
   * example: ['aibom', 'sbom']
   */
  requiredSubscriptions?: NonEmptyArray<keyof OrganizationSubscription>;
  /**
   * Optional redirect path for subscription failures.
   * If provided, users without required subscriptions will be redirected instead of seeing an error message.
   * example: '/ai-explorer'
   */
  redirectNonSubscriberTo?: string;
}

/**
 * A component that protects routes based on explicitly provided permissions.
 * * @example
 * // Using ROUTES constant for type safety
 * <ProtectedRoute requiredPermissions={['read:role']}>
 *   <RolesPage />
 * </ProtectedRoute>
 *
 * @example
 * // With subscription requirements and redirect on failure
 * <ProtectedRoute 
 *   requiredPermissions={['read:asset']} 
 *   requiredSubscriptions={['sbom']}
 *   redirectTo="/ai-explorer"
 * >
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * @example
 * // With dynamic route parameters
 * <Route path="/settings/roles/:roleUrn">
 *   <ProtectedRoute requiredPermissions={['read:role', 'update:role', 'delete:role', 'create:role']}>
 *     <RoleDetailsPage />
 *   </ProtectedRoute>
 * </Route>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredPermissions,
  availableOnlyForPlatformAdmins,
  requiredSubscriptions,
  redirectNonSubscriberTo,
  children,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isLoading, error } = useFetchUserPermissions({
    userId: user?.id ?? '',
  });
  const isAuthorized = useFetchUserHasPermissions({
    requiredPermissions,
    availableOnlyForPlatformAdmins,
  });
  const [organizationId] = useOrganizationId();
  const { isLoading: isLoadingOrganization, data: organization } = useFetchOrganization({
    organizationId: organizationId,
  });

  if (isLoading || isLoadingOrganization) {
    return (
      <Flex aria-label="loading-route-protection" justify="center" pt="48px" w="100%">
        <Loading customLoadingMessage={'Checking user permissions...'} />
      </Flex>
    );
  }

  // 1. Ensure user has required permissions
  if (!isAuthorized) {
    return (
      <Flex justify="center" w="100%">
        <ActionableCard
          Icon={<Icon icon="ban" iconSharp />}
          title={t('non-authorized.title')}
          subtitle={t('non-authorized.description')}
        />
      </Flex>
    );
  }  // 2. Ensure current organization has required subscriptions
  const isSubscribed = requiredSubscriptions
    ? hasOrganizationSubscriptions(organization?.subscriptions, requiredSubscriptions)
    : true;

  if (!isSubscribed) {
    // If redirectTo is provided, redirect instead of showing error message
    if (redirectNonSubscriberTo) {
      return <Navigate to={redirectNonSubscriberTo} replace />;
    }

    // Otherwise, show the default error message
    return (
      <Flex justify="center" w="100%">
        <ActionableCard
          Icon={<Icon icon="ban" iconSharp />}
          title={t('non-subscribed.title')}
          subtitle={t('non-subscribed.description')}
        />
      </Flex>
    );
  }

  return <>{children}</>;
};
